/* eslint-disable i18next/no-literal-string */
import {useState} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useQuery} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {usePageTitle} from '@/hooks/use-page-title'
import {useTypedEntityAttributes} from '@/hooks/use-typed-entity-attributes'
import {pickTypeValueLabel} from '@/lib/promoted-types-label'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardContent} from '@/components/ui/card'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {InlineRow} from '@/components/shared/InlineRow'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormField} from '@/components/shared/FormField'
import {PageBackButton} from '@/components/shared/PageBackButton'
import {SectionContainer} from '@/components/shared/SectionContainer'
import {AttributeFields} from '@/components/attributes/AttributeFields'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {AttributeValueType} from '@/types/attribute'
import type {AttributeDefinition, SetAttributeRequest} from '@/types/attribute-definition'
import {AttributeDefinitionResponseFields} from '@/types/attribute-definition'
import {ResourceResponseFields} from '@/types/resource'
import type {ResourceCreateRequest} from '@/types/resource'
import {useFormSubmit} from '@/hooks/use-form-submit'

function buildSetAttributeRequest(def: AttributeDefinition, raw: string): SetAttributeRequest {
    const value = raw.trim()
    switch (def.valueType) {
        case AttributeValueType.NUMBER:
            return {attributeName: def.name, valueType: def.valueType, valueNumber: Number(value)}
        case AttributeValueType.BOOLEAN:
            // eslint-disable-next-line i18next/no-literal-string -- boolean string-form sentinel from <input>
            return {attributeName: def.name, valueType: def.valueType, valueBoolean: value === 'true'}
        case AttributeValueType.DATE:
            return {attributeName: def.name, valueType: def.valueType, valueDate: value}
        case AttributeValueType.TIMESTAMP:
            return {attributeName: def.name, valueType: def.valueType, valueTimestamp: value}
        case AttributeValueType.STRING:
        case AttributeValueType.JSON:
        case AttributeValueType.REFERENCE:
            return {attributeName: def.name, valueType: def.valueType, valueString: value}
    }
}

/**
 * Create Resource page invoked from /systems/resources/by-type/{TYPE}/new.
 * The resourceType is locked from the URL; the form collects target-system +
 * nativeKey + displayName + dynamic attributes.
 */
export function CreateResource() {
    const {typeCode} = useParams<{typeCode: string}>()
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {resourceTypes} = useAppConfig()
    const {i18n: i18nInstance} = useTranslation()
    const lang = i18nInstance.language

    const resourceTypeCode = (typeCode ?? '').toUpperCase()
    const singularLabel = pickTypeValueLabel(resourceTypes, resourceTypeCode, lang) ?? labelOf(resourceTypes, resourceTypeCode)
    const pageTitle = `${i18n.common.create} ${singularLabel}`

    usePageTitle(pageTitle)

    const [targetSystemId, setTargetSystemId] = useState('')
    const [attributeValues, setAttributeValues] = useState<Record<string, string>>({})

    const {data: systemsData} = useQuery({
        queryKey: ['systems', 'for-resource-create'],
        queryFn: () => services.systems.list({size: 200}),
    })
    const availableSystems = systemsData?.content ?? []

    const {formDefinitions, templateAttributeNames} = useTypedEntityAttributes({
        entityType: AttributeEntityType.RESOURCE,
        typeCode: resourceTypeCode,
    })

    const renderDefinitions = formDefinitions.filter(d => d.name !== ResourceResponseFields.resourceType)

    const createMutation = useEntityMutation({
        mutationFn: (request: ResourceCreateRequest) => services.resources.create(request),
        successMessage: i18n.resources.createSuccess,
        onSuccess: (resource) => {
            navigate(routes.systems.resources.detail(resource.id))
        },
    })

    const {submit, fieldErrors} = useFormSubmit({
        fields: [
            {
                id: 'resource-system',
                label: i18n.resources.targetSystem,
                value: targetSystemId,
                required: true,
                focusId: 'resource-system',
            },
            ...renderDefinitions.map((def) => {
                const label = def.displayName || def.name
                const value = attributeValues[def.id] ?? ''
                // WHY: 1068 — the displayName surface is the required
                // dynamic `name` attribute that backs the resource_type
                // template `{name}`. Keep the legacy
                // `input-resource-display-name` testid via
                // AttributeFields' testIdMap (passed below) so callers
                // don't have to retarget.
                const focusId = def.name === AttributeDefinitionResponseFields.name
                    ? 'input-resource-display-name'
                    : def.name === ResourceResponseFields.nativeKey
                    ? 'input-resource-native-key'
                    : def.name === ResourceResponseFields.dataClassification
                    ? 'select-classification'
                    : `attr-${def.name}`
                return {
                    id: def.id,
                    label,
                    value,
                    required: def.required || templateAttributeNames.includes(def.name),
                    focusId,
                }
            }),
        ],
        onSubmit: () => {
            const getVal = (name: string) => {
                const def = formDefinitions.find(d => d.name === name)
                return def ? (attributeValues[def.id] ?? '').trim() : ''
            }
            const displayNameVal = getVal(AttributeDefinitionResponseFields.name)
            const nativeKeyVal = getVal(ResourceResponseFields.nativeKey)
            const dataClassificationVal = getVal(ResourceResponseFields.dataClassification)
            const parentIdVal = getVal(ResourceResponseFields.parent)

            const staticNames: readonly string[] = [
                ResourceResponseFields.nativeKey,
                AttributeDefinitionResponseFields.name,
                ResourceResponseFields.resourceType,
                ResourceResponseFields.dataClassification,
                ResourceResponseFields.parent,
            ]
            const dynamicDefs = formDefinitions.filter(def => !staticNames.includes(def.name))

            const attributes: SetAttributeRequest[] = dynamicDefs
                .map((def) => {
                    const raw = attributeValues[def.id] ?? ''
                    if (!raw.trim()) return null
                    return buildSetAttributeRequest(def, raw)
                })
                .filter((r): r is SetAttributeRequest => r != null)

            createMutation.mutate({
                targetSystemId,
                resourceType: resourceTypeCode,
                nativeKey: nativeKeyVal,
                displayName: displayNameVal,
                dataClassification: dataClassificationVal || null,
                parentId: parentIdVal || null,
                attributes,
            })
        },
    })

    return (
        <SectionContainer>
            <PageBackButton to={routes.systems.resources.byType(resourceTypeCode)}/>

            <Card className="max-w-2xl">
                <CardHeader>
                    <CardTitle data-testid="card-title-create-resource">{pageTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={submit}>
                        <FormGrid>
                            <FormField
                                label={i18n.resources.targetSystem}
                                htmlFor="resource-system"
                                required
                                error={fieldErrors['resource-system']}
                            >
                                <Select value={targetSystemId} onValueChange={setTargetSystemId}>
                                    <SelectTrigger id="resource-system" data-testid="select-target-system">
                                        <SelectValue placeholder={i18n.resources.targetSystem}/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableSystems.map((sys) => (
                                            <SelectItem key={sys.id} value={sys.id} data-testid={`system-${sys.id}`}>
                                                {sys.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <AttributeFields
                                definitions={renderDefinitions}
                                values={attributeValues}
                                onChange={(defId, v) =>
                                    setAttributeValues((s) => ({...s, [defId]: v}))}
                                templateAttributeNames={templateAttributeNames}
                                errors={fieldErrors}
                                testIdMap={{
                                    name: 'input-resource-display-name',
                                }}
                            />
                        </FormGrid>

                        <InlineRow className="justify-end pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                data-testid="btn-cancel-create-resource"
                                onClick={() => navigate(routes.systems.resources.byType(resourceTypeCode))}
                            >
                                {i18n.common.cancel}
                            </Button>
                            <Button
                                type="submit"
                                data-testid="btn-submit-create-resource"
                                disabled={createMutation.isPending}
                            >
                                {pageTitle}
                            </Button>
                        </InlineRow>
                    </form>
                </CardContent>
            </Card>
        </SectionContainer>
    )
}

// WHY: testid generator can't follow `focusId = ... ? 'kebab-string' : ...`
// variables — the data-testid is rendered by AttributeFields/AttributeValueInput
// from htmlFor strings assembled here. Register explicitly via the
// `scan-testid:` annotation pattern (issue 1023).
// scan-testid: input-resource-display-name
// scan-testid: input-resource-native-key
// scan-testid: select-classification
// scan-testid-template: classification-${c_code}
